import { toast } from '@tamagui/toast/v2';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import { ScrollView, Text, XStack, YStack } from 'tamagui';

import { PlanTag } from '@/components/MembershipPlanCard';
import PSButton from '@/components/ui/PSButton';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import { color, space } from '@/constants/tokens';
import { asApiError } from '@/src/api/apiError';
import type { UserMembership } from '@/src/api/authApi';
import type { Membership } from '@/src/api/membershipsApi';
import type { PollMembershipState } from '@/src/hooks/usePollMembership';
import { formatDateWithYear } from '@/src/utils/dateFormatter';
import { normalizeSocialLink } from '@/src/utils/validation';

/**
 * The body of `app/memberships/purchase/status.tsx`, drawn from props alone so
 * the gallery can show every state — the route owns the poll, the session, the
 * header and the exit.
 *
 * ── The design (designer's mock, Aug 2026) ──
 * One template for all three states: a gradient panel taking the top 70% of the
 * screen with the Access Pass wordmark up top, the pass name as an eyebrow, the
 * STATE as the headline, one line of detail, and a CTA pinned to the panel's
 * bottom. Only those four strings change between confirming / activated /
 * unconfirmed, so nothing moves as the poll settles — the CTA does the waiting
 * in its own loading state.
 *
 * The Trial Pass wears its own panel (Oct 2026): a pinker glow, and the TRIAL tag
 * in place of the pass name.
 */
type MembershipPurchaseStatusProps = {
  poll: PollMembershipState;
  /** The plan it was bought from — stands in until the membership lands. */
  plan: Pick<Membership, 'name' | 'kind'> | undefined;
  /** Signed in with no social link on file. */
  canAskForSocialLink: boolean;
  onShareSocialLink: (url: string) => Promise<void>;
  onLeave: () => void;
  /** The transparent header floats over the panel; its height pads the top. */
  headerHeight: number;
};

export default function MembershipPurchaseStatus({
  poll,
  plan,
  canAskForSocialLink,
  onShareSocialLink,
  onLeave,
  headerHeight,
}: MembershipPurchaseStatusProps) {
  const { height } = useWindowDimensions();

  // Settled is not the same as live — a membership can stop moving somewhere
  // other than `active` (a halted mandate, a cancellation).
  const membership =
    poll.status === 'success' && poll.membership.status === 'active'
      ? poll.membership
      : null;
  const isConfirming = poll.status === 'loading';

  // Both can be missing (plans still loading), hence the fallbacks.
  const passName = membership?.plan.name ?? plan?.name ?? 'Membership';
  const kind = membership?.plan.kind ?? plan?.kind ?? 'access_pass';
  const panelHeight = height * PANEL_HEIGHT_RATIO;
  const glow = PANEL_GLOW[kind];

  const headline = isConfirming
    ? 'Confirming'
    : membership
      ? 'Activated'
      : 'Not confirmed';

  const detail = isConfirming
    ? CONFIRMING_MESSAGE
    : membership
      ? renewalLine(membership)
      : // The server's own words when it gave one, ours when we simply stopped
        // waiting or the membership settled somewhere unexpected. Neither is a
        // reason to pay again, which the copy says outright.
        (poll.error?.message ?? UNCONFIRMED_MESSAGE);

  return (
    <ScrollView
      flex={1}
      bg={'$color.background'}
      showsVerticalScrollIndicator={false}
      // The socials card sits low enough that the keyboard would cover it.
      automaticallyAdjustKeyboardInsets
      contentContainerStyle={{ pb: space.xxl }}
    >
      <YStack
        height={panelHeight}
        borderBottomLeftRadius={'$base'}
        borderBottomRightRadius={'$base'}
        overflow="hidden"
      >
        {/* Raised rather than given an off-panel start point: web draws a
            gradient from its angle alone and ignores where it starts. */}
        <LinearGradient
          colors={[glow.from, color.brandDark]}
          locations={[0, (glow.blackAt - glow.fromAt) / (1 - glow.fromAt)]}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={[StyleSheet.absoluteFill, { top: glow.fromAt * panelHeight }]}
        />

        <YStack px={'$space.xl'} pt={headerHeight + space.xxl}>
          <Image
            source={require('@/assets/images/access-pass-logo.png')}
            style={{ width: 240, height: 100 }}
            contentFit="contain"
          />
        </YStack>

        {/* The copy+CTA own the panel's bottom, the wordmark its top — this
            fills the slack between them so the panel keeps its shape
            whichever state is showing. */}
        <YStack flex={1} />

        <YStack px={'$space.xl'} gap={'$space.md'} mb={'$space.xxxl'}>
          {kind === 'trial' ? (
            <PlanTag kind={kind} />
          ) : (
            <PSText.H3 color={'$color.textWhite'}>{passName}</PSText.H3>
          )}

          <Text
            fontSize={'$veryLarge'}
            fontWeight={'$semibold'}
            lineHeight={HEADLINE_LINE_HEIGHT}
            color={'$color.textWhite'}
          >
            {headline}
          </Text>
          {detail && (
            <PSText.Body3 color={'$color.textWhiteMuted'}>
              {detail}
            </PSText.Body3>
          )}
        </YStack>

        <YStack px={'$space.xl'} pb={'$space.xl'}>
          {isConfirming ? (
            // Full opacity, unpressable, progressive verb — the CTA slot does
            // the waiting so nothing moves when the poll settles.
            <PSButton
              fullWidth
              title={PUTTING_SCENES_CTA}
              loading
              loadingTitle="Confirming"
            />
          ) : (
            <PSButton
              fullWidth
              title={membership ? PUTTING_SCENES_CTA : 'Back to home'}
              onPress={onLeave}
            />
          )}
        </YStack>
      </YStack>

      {/* Only once it's live — there's nothing to celebrate mid-confirm, and a
          purchase we couldn't confirm is the wrong moment to ask for a follow. */}
      {membership && canAskForSocialLink && (
        <SocialsCard onShare={onShareSocialLink} />
      )}
    </ScrollView>
  );
}

const PUTTING_SCENES_CTA = 'Start Putting Scenes';

// Ours, not the server's: the three platforms are the CLIENT's rule (see
// normalizeSocialLink), so the sentence that teaches it is the client's too.
const SOCIAL_LINK_HINT = 'Paste your Instagram, X or LinkedIn link.';

const CONFIRMING_MESSAGE =
  'Your payment has gone through to Razorpay. Confirming it usually takes a few seconds.';

const UNCONFIRMED_MESSAGE =
  "We couldn't confirm this membership. If you've been charged it will appear on its own — there's no need to pay again.";

/** The mock's panel is 70% of the screen; the rest is page. */
const PANEL_HEIGHT_RATIO = 0.7;
/**
 * Both fade to onyx, standing in for the mocks' black. `fromAt` / `blackAt` are
 * fractions of the panel's height: the trial's full pink sits ABOVE the panel,
 * so its top edge is already part-way dark — read off the mock's pixels, as its
 * exported stops don't match what it draws.
 */
const PANEL_GLOW: Record<
  Membership['kind'],
  { from: string; fromAt: number; blackAt: number }
> = {
  access_pass: { from: color.brand, fromAt: 0, blackAt: 0.78 },
  trial: { from: color.brandSecondary, fromAt: -0.35, blackAt: 0.75 },
};
// $veryLarge has no lineHeight in the font config, so web falls back to the
// flat 20px default and clips the glyph — same override as app/bookings/[id].
const HEADLINE_LINE_HEIGHT = Platform.select({ web: 56 });

/** What the membership does next — the one date that isn't null. */
const renewalLine = (membership: UserMembership): string | null => {
  if (membership.renewsAt)
    return `Renews on ${formatDateWithYear(membership.renewsAt)}`;
  // Only an ending membership reports `expiresAt`, so this holds for any of them.
  if (membership.expiresAt)
    return `Valid until ${formatDateWithYear(membership.expiresAt)}.`;
  return null;
};

/**
 * The one thing asked of a brand-new member: an Instagram / X / LinkedIn link.
 *
 * The card owns the field and nothing else — WHICH links count is
 * [[normalizeSocialLink]]'s judgment (the server only checks "is a URL"), and
 * saving is `onShare`. It sends what the validator RETURNED, not what was
 * typed, so a missing scheme is fixed rather than rejected.
 *
 * House rhythm on the error: cleared on focus, re-checked on blur, checked again
 * on submit — a paste can reach Submit without the field ever blurring. The
 * server's own 4xx message replaces ours under the field, since a verdict on the
 * value we sent is more specific than "we don't take that"; a network or 5xx
 * failure isn't about the link at all and goes to a toast.
 *
 * Submitting settles the card for good rather than clearing it: a second link
 * would just overwrite the first, and the field re-arming reads as "that didn't
 * work".
 */
function SocialsCard({ onShare }: { onShare: (url: string) => Promise<void> }) {
  const [link, setLink] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isShared, setIsShared] = useState(false);

  const share = async () => {
    const url = normalizeSocialLink(link);
    if (!url) {
      setError(SOCIAL_LINK_HINT);
      return;
    }

    setIsSharing(true);
    try {
      await onShare(url);
      setIsShared(true);
    } catch (caught) {
      const failure = asApiError(caught);
      if (failure.kind === 'api') setError(failure.message);
      else toast.error(failure.message);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <YStack
      mx={'$space.xl'}
      mt={'$space.xl'}
      p={'$space.lg'}
      gap={'$space.sm'}
      rounded={'$base'}
      borderWidth={'$borderWidth.hairline'}
      borderColor={'$borderColor'}
    >
      <YStack gap={'$space.xs'}>
        <PSText.H2 color={'$color.textDark'}>
          {isShared ? 'Thanks for sharing!' : 'Share your socials'}
        </PSText.H2>
        <PSText.Body3 color={'$color.textDark'}>
          {isShared ? "We'll follow you back." : "We'd love to follow you!"}
        </PSText.Body3>
      </YStack>

      {!isShared && (
        // Top-aligned, not stretched: the validation message grows the field's
        // block, and a Submit stretched to cover it would tower over the input.
        <XStack gap={'$space.sm'} items="flex-start">
          {/* PSInput stretches but doesn't flex — the wrapper gives it the row. */}
          <YStack flex={1}>
            <PSInput
              type="url"
              value={link}
              onChangeText={setLink}
              placeholder="Paste social link"
              error={error ?? undefined}
              onFocus={() => setError(null)}
              onBlur={() => {
                if (link.trim() && !normalizeSocialLink(link))
                  setError(SOCIAL_LINK_HINT);
              }}
            />
          </YStack>
          <PSButton
            title="Submit"
            loading={isSharing}
            loadingTitle="Sharing"
            disabled={!link.trim()}
            onPress={share}
          />
        </XStack>
      )}
    </YStack>
  );
}
